import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AcademicCapIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  StarIcon,
  ClockIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const GrantBasics = ({ userRole }) => {
  const [expandedSection, setExpandedSection] = useState('types');
  const [completedLessons, setCompletedLessons] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(0);

  const grantTypes = [
    {
      id: 'seed',
      name: 'Seed Funding',
      description: 'Early-stage funding for concept validation and initial development',
      amount: 'P5K - P50K',
      timeline: '3-6 months',
      requirements: ['Business plan', 'Market research', 'Team background'],
      icon: CurrencyDollarIcon,
      color: 'green',
    },
    {
      id: 'startup',
      name: 'Startup Grants',
      description: 'Funding for established startups ready to scale',
      amount: 'P50K - P500K',
      timeline: '6-12 months',
      requirements: ['Proven concept', 'Revenue model', 'Growth metrics'],
      icon: AcademicCapIcon,
      color: 'blue',
    },
    {
      id: 'innovation',
      name: 'Innovation Grants',
      description: 'Research and development funding for innovative solutions',
      amount: 'P100K - P1M',
      timeline: '12-24 months',
      requirements: ['Innovation proof', 'Technical feasibility', 'Market potential'],
      icon: LightBulbIcon,
      color: 'purple',
    },
    {
      id: 'social',
      name: 'Social Impact Grants',
      description: 'Funding for businesses with social or environmental impact',
      amount: 'P25K - P250K',
      timeline: '6-18 months',
      requirements: ['Impact measurement', 'Social mission', 'Sustainability plan'],
      icon: UserGroupIcon,
      color: 'orange',
    },
  ];

  const fundingStages = [
    {
      stage: 'Discovery',
      description: 'Research and identify funding opportunities',
      activities: ['Market research', 'Grant identification', 'Eligibility check'],
      duration: '2-4 weeks',
      icon: InformationCircleIcon,
    },
    {
      stage: 'Preparation',
      description: 'Develop application materials and strategy',
      activities: ['Business plan', 'Financial projections', 'Supporting documents'],
      duration: '4-8 weeks',
      icon: DocumentTextIcon,
    },
    {
      stage: 'Application',
      description: 'Submit application and follow up',
      activities: ['Form completion', 'Document submission', 'Application review'],
      duration: '1-2 weeks',
      icon: CheckCircleIcon,
    },
    {
      stage: 'Review',
      description: 'Application evaluation and decision process',
      activities: ['Panel review', 'Interviews', 'Due diligence'],
      duration: '4-12 weeks',
      icon: ClockIcon,
    },
    {
      stage: 'Award',
      description: 'Grant award and fund disbursement',
      activities: ['Contract signing', 'Fund disbursement', 'Reporting setup'],
      duration: '2-4 weeks',
      icon: StarIcon,
    },
  ];

  const successTips = [
    {
      category: 'Research & Preparation',
      tips: [
        'Start early - grant applications take time',
        'Read guidelines thoroughly',
        'Match your project to grant objectives',
        'Gather all required documents in advance',
      ],
    },
    {
      category: 'Writing & Presentation',
      tips: [
        'Write clearly and concisely',
        'Use data and metrics to support claims',
        'Follow formatting requirements exactly',
        'Have someone else review your application',
      ],
    },
    {
      category: 'Follow-up & Persistence',
      tips: [
        'Submit before the deadline',
        'Follow up on application status',
        'Be prepared for interviews',
        'Learn from rejections and improve',
      ],
    },
  ];

  const eligibilityCriteria = [
    {
      category: 'Business Requirements',
      criteria: [
        'Registered business entity',
        'Valid business license',
        'Minimum 6 months of operation',
        'Clear business model',
      ],
    },
    {
      category: 'Financial Requirements',
      criteria: [
        'Financial statements',
        'Cash flow projections',
        'Use of funds plan',
        'Matching funds (if required)',
      ],
    },
    {
      category: 'Team Requirements',
      criteria: [
        'Experienced management team',
        'Relevant industry expertise',
        'Commitment to project success',
        'No conflicts of interest',
      ],
    },
  ];

  const toggleSection = sectionId => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const markLessonComplete = lessonId => {
    if (!completedLessons.includes(lessonId)) {
      setCompletedLessons([...completedLessons, lessonId]);
      toast.success('Lesson completed! Keep up the great work!');
    }
  };

  const getProgressPercentage = () => {
    const totalLessons = 4;
    return Math.round((completedLessons.length / totalLessons) * 100);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header Card with Progress */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
              <AcademicCapIcon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Grant Basics</h1>
              <p className="mt-1 text-sm text-gray-600 sm:text-base">
                Master the fundamentals of grant funding
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-6 py-4 sm:px-8">
            <div className="text-xs font-medium text-gray-600 sm:text-sm">Progress</div>
            <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
              {getProgressPercentage()}%
            </div>
          </div>
        </div>

        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${getProgressPercentage()}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="h-full rounded-full bg-blue-600"
          />
        </div>
      </motion.div>

      {/* Types of Grants Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
      >
        <div
          className="cursor-pointer border-b border-gray-200 bg-gray-50 p-6 transition-colors hover:bg-gray-100 sm:p-8"
          onClick={() => toggleSection('types')}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
                <CurrencyDollarIcon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Types of Grants</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Explore different funding opportunities
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {completedLessons.includes('types') && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-600 sm:h-10 sm:w-10">
                  <CheckCircleIcon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                </div>
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-600 transition-colors hover:bg-gray-100">
                {expandedSection === 'types' ? (
                  <ChevronDownIcon className="h-6 w-6" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6" />
                )}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {expandedSection === 'types' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-white p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {grantTypes.map((type, index) => {
                  const TypeIcon = type.icon;
                  return (
                    <motion.div
                      key={type.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="mb-4 flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600">
                          <TypeIcon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{type.name}</h3>
                      </div>
                      <p className="mb-6 text-gray-600">{type.description}</p>
                      <div className="mb-6 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Amount:</span>
                          <span className="text-sm font-semibold text-gray-900">{type.amount}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Timeline:</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {type.timeline}
                          </span>
                        </div>
                      </div>
                      <div>
                        <h4 className="mb-3 text-sm font-semibold text-gray-900">Requirements:</h4>
                        <ul className="space-y-2.5">
                          {type.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400"></div>
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              <div className="mt-8 text-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => markLessonComplete('types')}
                  className="rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                >
                  Mark as Complete
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      {/* Funding Stages Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
      >
        <div
          className="cursor-pointer border-b border-gray-200 bg-gray-50 p-6 transition-colors hover:bg-gray-100 sm:p-8"
          onClick={() => toggleSection('stages')}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
                <ClockIcon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Funding Stages</h2>
                <p className="mt-1 text-sm text-gray-600">Navigate the grant application process</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {completedLessons.includes('stages') && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-600 sm:h-10 sm:w-10">
                  <CheckCircleIcon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                </div>
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-600 transition-colors hover:bg-gray-100">
                {expandedSection === 'stages' ? (
                  <ChevronDownIcon className="h-6 w-6" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6" />
                )}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {expandedSection === 'stages' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-white p-6 sm:p-8"
            >
              <div className="space-y-6">
                {fundingStages.map((stage, index) => {
                  const StageIcon = stage.icon;
                  return (
                    <motion.div
                      key={stage.stage}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:flex-row sm:gap-6"
                    >
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
                            <span className="text-base font-bold text-white sm:text-lg">
                              {index + 1}
                            </span>
                          </div>
                          {index < fundingStages.length - 1 && (
                            <div className="absolute left-1/2 top-full mt-2 h-8 w-0.5 -translate-x-1/2 bg-gray-300 sm:h-12"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
                            <StageIcon className="h-5 w-5 text-gray-600" />
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 sm:text-xl">
                            {stage.stage}
                          </h3>
                          <span className="rounded-full border border-gray-300 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="mb-4 text-gray-600">{stage.description}</p>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
                          <h4 className="mb-3 text-sm font-semibold text-gray-900">
                            Key Activities:
                          </h4>
                          <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                            {stage.activities.map((activity, idx) => (
                              <li
                                key={idx}
                                className="flex items-center gap-2.5 text-sm text-gray-700"
                              >
                                <div className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400"></div>
                                <span>{activity}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              <div className="mt-8 text-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => markLessonComplete('stages')}
                  className="rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                >
                  Mark as Complete
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Success Tips Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
      >
        <div
          className="cursor-pointer border-b border-gray-200 bg-gray-50 p-6 transition-colors hover:bg-gray-100 sm:p-8"
          onClick={() => toggleSection('tips')}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
                <LightBulbIcon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Success Tips</h2>
                <p className="mt-1 text-sm text-gray-600">Best practices for grant success</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {completedLessons.includes('tips') && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-600 sm:h-10 sm:w-10">
                  <CheckCircleIcon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                </div>
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-600 transition-colors hover:bg-gray-100">
                {expandedSection === 'tips' ? (
                  <ChevronDownIcon className="h-6 w-6" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6" />
                )}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {expandedSection === 'tips' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-white p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {successTips.map((category, index) => (
                  <motion.div
                    key={category.category}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                        <LightBulbIcon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{category.category}</h3>
                    </div>
                    <ul className="space-y-3">
                      {category.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                          <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400"></div>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => markLessonComplete('tips')}
                  className="rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                >
                  Mark as Complete
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Eligibility Criteria Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
      >
        <div
          className="cursor-pointer border-b border-gray-200 bg-gray-50 p-6 transition-colors hover:bg-gray-100 sm:p-8"
          onClick={() => toggleSection('eligibility')}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 sm:h-14 sm:w-14">
                <CheckCircleIcon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
                  Eligibility Criteria
                </h2>
                <p className="mt-1 text-sm text-gray-600">Requirements for grant applications</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {completedLessons.includes('eligibility') && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-600 sm:h-10 sm:w-10">
                  <CheckCircleIcon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                </div>
              )}
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-gray-600 transition-colors hover:bg-gray-100">
                {expandedSection === 'eligibility' ? (
                  <ChevronDownIcon className="h-6 w-6" />
                ) : (
                  <ChevronRightIcon className="h-6 w-6" />
                )}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {expandedSection === 'eligibility' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-white p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {eligibilityCriteria.map((category, index) => (
                  <motion.div
                    key={category.category}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                        <CheckCircleIcon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{category.category}</h3>
                    </div>
                    <ul className="space-y-3">
                      {category.criteria.map((criterion, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                          <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                          <span>{criterion}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => markLessonComplete('eligibility')}
                  className="rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                >
                  Mark as Complete
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Completion Card */}
      {getProgressPercentage() === 100 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm sm:p-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-600 sm:h-20 sm:w-20"
          >
            <CheckCircleIcon className="h-10 w-10 text-white sm:h-12 sm:w-12" />
          </motion.div>
          <h3 className="mb-3 text-2xl font-bold text-gray-900 sm:text-3xl">Congratulations!</h3>
          <p className="mb-8 text-gray-600 sm:text-lg">You've completed the Grant Basics course.</p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="rounded-lg bg-blue-600 px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-blue-700"
          >
            Continue to Application Tutorials
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default GrantBasics;
